import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface ImageCrop { x: number; y: number; w: number; h: number }

interface ImageCropBoxProps {
  src: string;
  crop: ImageCrop;
  /** Fired on release, not on every mouse move — one crop edit is one undo step. */
  onChange: (crop: ImageCrop) => void;
}

const FULL: ImageCrop = { x: 0, y: 0, w: 1, h: 1 };
const MIN = 0.05;
const HANDLES = ['tl', 'tr', 'bl', 'br'] as const;

/**
 * The crop picker in the image overlay panel. It works in fractions of the *source image*, drawn
 * at the image's own aspect so the rect the author drags is the rect that gets rendered.
 *
 * The crop does not move or resize the overlay on the frame — the box on the video keeps the size
 * it was given, and cropping only changes what fills it.
 */
const ImageCropBox: React.FC<ImageCropBoxProps> = ({ src, crop, onChange }) => {
  const [local, setLocal] = useState<ImageCrop>(crop);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<string | null>(null);

  // Follow the image the panel is showing — selecting a different overlay must not leave the
  // previous one's rect on screen.
  useEffect(() => { setLocal(crop); }, [crop.x, crop.y, crop.w, crop.h, src]);

  const drag = useCallback((e: React.MouseEvent, handle: string) => {
    e.preventDefault();
    e.stopPropagation();
    const surface = surfaceRef.current;
    if (!surface) return;
    const rect = surface.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const start = { ...local };
    setDragging(handle);
    let latest = start;

    const onMouseMove = (ev: MouseEvent) => {
      const dx = (ev.clientX - startX) / rect.width;
      const dy = (ev.clientY - startY) / rect.height;
      const c = { ...start };

      if (handle === 'move') {
        c.x = Math.max(0, Math.min(1 - start.w, start.x + dx));
        c.y = Math.max(0, Math.min(1 - start.h, start.y + dy));
      } else {
        if (handle.includes('l')) {
          const nx = Math.max(0, Math.min(start.x + start.w - MIN, start.x + dx));
          c.w = start.x + start.w - nx;
          c.x = nx;
        }
        if (handle.includes('r')) {
          c.w = Math.max(MIN, Math.min(1 - start.x, start.w + dx));
        }
        if (handle.includes('t')) {
          const ny = Math.max(0, Math.min(start.y + start.h - MIN, start.y + dy));
          c.h = start.y + start.h - ny;
          c.y = ny;
        }
        if (handle.includes('b')) {
          c.h = Math.max(MIN, Math.min(1 - start.y, start.h + dy));
        }
      }
      latest = c;
      setLocal(c);
    };
    const onMouseUp = () => {
      setDragging(null);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      onChange(latest);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [local, onChange]);

  const cropped = local.x > 0 || local.y > 0 || local.w < 1 || local.h < 1;

  return (
    <div className="image-crop-box">
      <div className="image-crop-surface" ref={surfaceRef}>
        <img src={src} alt="crop source" draggable={false} />

        <div className="image-crop-mask" style={{ left: 0, top: 0, width: '100%', height: `${local.y * 100}%` }} />
        <div className="image-crop-mask" style={{ left: 0, top: `${(local.y + local.h) * 100}%`, width: '100%', bottom: 0 }} />
        <div className="image-crop-mask" style={{ left: 0, top: `${local.y * 100}%`, width: `${local.x * 100}%`, height: `${local.h * 100}%` }} />
        <div className="image-crop-mask" style={{ left: `${(local.x + local.w) * 100}%`, top: `${local.y * 100}%`, right: 0, height: `${local.h * 100}%` }} />

        <div
          className="image-crop-rect"
          style={{
            left: `${local.x * 100}%`, top: `${local.y * 100}%`,
            width: `${local.w * 100}%`, height: `${local.h * 100}%`,
          }}
          onMouseDown={(e) => drag(e, 'move')}
        >
          {HANDLES.map(h => (
            <div
              key={h}
              className={`image-crop-handle image-crop-handle-${h} ${dragging === h ? 'active' : ''}`}
              onMouseDown={(e) => drag(e, h)}
            />
          ))}
        </div>
      </div>

      <div className="image-crop-footer">
        <span>{cropped ? `${Math.round(local.w * 100)}% × ${Math.round(local.h * 100)}%` : 'Full image'}</span>
        <button
          className="btn-sm"
          disabled={!cropped}
          onClick={() => { setLocal(FULL); onChange(FULL); }}
        >
          Reset
        </button>
      </div>
    </div>
  );
};

export default ImageCropBox;
